import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type { Driver } from 'driver.js'
import 'modules/get_started/lib/tours/tourStyles.css'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/Tracking'
import { events } from 'modules/get_started/lib/events'
import {
  getCompletedTasks,
  markTaskCompleted,
  resetState,
} from 'modules/get_started/hooks/useDemoTourTasks'
import {
  getTaskSteps,
  getTaskRoute,
  preTagElements,
  type TourCallbacks,
} from 'modules/get_started/lib/tours/timeTrackingTour'
import { waitForElement } from 'modules/get_started/lib/tours/tourUtils'

type TourView = 'welcome' | 'touring' | 'booking' | 'hidden'

const WEBPAGE_MEETINGS_API_URL = 'https://factorialhr.com/api/hubspot/meetings'
const HUBSPOT_MEETINGS_URL = 'https://meetings.hubspot.com'
const FALLBACK_MEETING_URL = 'https://meetings.hubspot.com/factorial-demo'

interface UseDemoTourReturn {
  view: TourView
  activeTaskKey: string | null
  completedTasks: string[]
  currentStep: number
  showConfetti: boolean
  calendarUrl: string | null
  meetingModalOpen: boolean
  startTour: () => void
  dismissWelcome: () => void
  startTask: (taskKey: string) => void
  stopTask: () => void
  restartTour: () => void
  showBookingModal: () => void
  closeBookingModal: () => void
  bookMeeting: () => void
  closeMeetingModal: () => void
}

export function useDemoTour({ enabled = true }: { enabled?: boolean } = {}): UseDemoTourReturn {
  const navigate = useNavigate()
  const location = useLocation()
  const [view, setView] = useState<TourView>(() => {
    try {
      const dismissed = localStorage.getItem('demo_tour_welcome_dismissed')
      return dismissed === '1' ? 'touring' : 'welcome'
    } catch {
      return 'welcome'
    }
  })
  const [activeTaskKey, setActiveTaskKey] = useState<string | null>(null)
  const [completedTasks, setCompletedTasks] = useState<string[]>(getCompletedTasks)
  const [totalSteps, setTotalSteps] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [showConfetti, setShowConfetti] = useState(false)
  const [calendarUrl, setCalendarUrl] = useState<string | null>(null)
  const [meetingModalOpen, setMeetingModalOpen] = useState(false)
  const driverRef = useRef<Driver | null>(null)
  const pendingTaskRef = useRef<string | null>(null)
  const resumeTourRef = useRef<(task: string, step: number) => Promise<void>>(() =>
    Promise.resolve()
  )
  const skipResetRef = useRef(false)
  const startTaskRef = useRef<(taskKey: string) => void>(() => {})

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const params = new URLSearchParams({ country: 'ES', companySize: '1000' })
    fetch(`${WEBPAGE_MEETINGS_API_URL}?${String(params)}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (
          data &&
          typeof data === 'object' &&
          'sdrName' in data &&
          typeof data.sdrName === 'string'
        ) {
          setCalendarUrl(`${HUBSPOT_MEETINGS_URL}/${encodeURIComponent(data.sdrName)}`)
        }
      })
      .catch(() => {})
    return () => controller.abort()
  }, [enabled])

  const destroyDriver = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy()
      driverRef.current = null
    }
  }, [])

  const showBookingModal = useCallback(() => {
    destroyDriver()
    setActiveTaskKey(null)
    setView('booking')
    track(events.viewBookingModalDemoTour)
  }, [destroyDriver])

  const tourCallbacksRef = useRef<TourCallbacks>({
    onShowConfetti: () => {
      setShowConfetti(true)
      setTimeout(() => setShowConfetti(false), 4000)
    },
    onShowBookingModal: () => {},
    navigate: () => {},
    skipReset: () => {},
    resumeTour: () => {},
  })

  useEffect(() => {
    tourCallbacksRef.current.onShowBookingModal = () => showBookingModal()
    tourCallbacksRef.current.navigate = navigate
    tourCallbacksRef.current.skipReset = () => {
      skipResetRef.current = true
    }
    tourCallbacksRef.current.resumeTour = (task: string, step: number) => {
      void resumeTourRef.current(task, step)
    }
  }, [showBookingModal, navigate])

  const launchDriver = useCallback(
    async (taskKey: string, startStep = 0) => {
      destroyDriver()

      const [{ driver: createDriver }, _css] = await Promise.all([
        import('driver.js'),
        import('driver.js/dist/driver.css'),
      ])

      const driverInstance = createDriver({
        showProgress: false,
        showButtons: ['next'],
        allowClose: false,
        animate: true,
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        stagePadding: 8,
        stageRadius: 8,
        smoothScroll: true,
        popoverClass: 'demo-tour-popover',
        nextBtnText: i18n.t('growth.get_started.demo_tour.continue_btn'),
        doneBtnText: i18n.t('growth.get_started.demo_tour.continue_btn'),
        onPopoverRender: (popover) => {
          const footer = popover.footer
          if (!footer) return
          const existing = footer.querySelector('.demo-tour-quit-btn')
          if (existing) return
          const quitBtn = document.createElement('button')
          quitBtn.className = 'demo-tour-quit-btn'
          quitBtn.textContent = i18n.t('growth.get_started.demo_tour.quit_tour')
          quitBtn.addEventListener('click', () => {
            track(events.stopDemoTour, { task_key: taskKey })
            driverInstance.destroy()
          })
          footer.prepend(quitBtn)
        },
        onHighlightStarted: () => {
          setCurrentStep(driverInstance.getActiveIndex() ?? 0)
        },
        onDestroyed: () => {
          driverRef.current = null
          if (skipResetRef.current) {
            skipResetRef.current = false
            return
          }
          setActiveTaskKey(null)
          setView('touring')
          setCurrentStep(0)
          setTotalSteps(0)
        },
      })

      const steps = getTaskSteps(taskKey, driverInstance, tourCallbacksRef.current)
      if (steps.length === 0) return

      const lastStep = steps[steps.length - 1]
      if (!lastStep) return
      const originalOnNext = lastStep.popover?.onNextClick
      if (!lastStep.popover) {
        lastStep.popover = {}
      }
      lastStep.popover.onNextClick = (...args) => {
        if (originalOnNext) {
          originalOnNext(...args)
        }
        markTaskCompleted(taskKey)
        const updatedCompleted = getCompletedTasks()
        setCompletedTasks(updatedCompleted)
        setActiveTaskKey(null)
        destroyDriver()
        setCurrentStep(0)
        setTotalSteps(0)
        track(events.completeDemoTourTask, { task_key: taskKey })

        if (taskKey === 'filter_important') {
          const completedSet = new Set(updatedCompleted)
          const taskOrder = ['filter_important', 'detect_alerts', 'bulk_approve']
          const nextTask = taskOrder.find((k) => !completedSet.has(k))
          if (nextTask) {
            setTimeout(() => startTaskRef.current(nextTask), 500)
          }
        }
      }

      driverInstance.setSteps(steps)
      driverRef.current = driverInstance
      setTotalSteps(steps.length)
      setCurrentStep(startStep)
      setActiveTaskKey(taskKey)
      setView('touring')
      if (startStep === 0) {
        track(events.startDemoTourTask, { task_key: taskKey })
      }

      setTimeout(() => {
        const waitSelector =
          startStep === 0
            ? 'tbody, nav a[href*="/attendance/time-tracking"]'
            : '[data-intercom-target="attendance-row-toggle"]'
        const timeout = startStep === 0 ? 5000 : 10000
        void waitForElement(waitSelector, timeout)
          .then(() => {
            preTagElements(taskKey)
            driverInstance.drive(startStep)
          })
          .catch(() => {
            preTagElements(taskKey)
            driverInstance.drive(startStep)
          })
      }, 300)
    },
    [destroyDriver]
  )

  useEffect(() => {
    resumeTourRef.current = launchDriver
  }, [launchDriver])

  useEffect(() => {
    let resumeData: string | null = null
    try {
      resumeData = sessionStorage.getItem('demo-tour-resume')
    } catch {}
    if (resumeData && location.pathname.includes('/attendance/time-tracking/employee/')) {
      try {
        sessionStorage.removeItem('demo-tour-resume')
      } catch {}
      try {
        const parsed = JSON.parse(resumeData)
        const task = parsed?.task
        const step = parsed?.step
        if (typeof task === 'string' && typeof step === 'number') {
          setTimeout(() => {
            void resumeTourRef.current(task, step)
          }, 1000)
          return
        }
      } catch {}
    }

    const pending = pendingTaskRef.current
    if (!pending) return

    const targetRoute = getTaskRoute(pending)
    if (targetRoute && location.pathname.startsWith(targetRoute)) {
      pendingTaskRef.current = null
      void waitForElement('tbody, nav a[href*="/attendance/time-tracking"]', 5000)
        .then(() => void launchDriver(pending))
        .catch(() => void launchDriver(pending))
    }
  }, [location.pathname, launchDriver])

  const startTask = useCallback(
    (taskKey: string) => {
      const targetRoute = getTaskRoute(taskKey)
      if (targetRoute && !location.pathname.startsWith(targetRoute)) {
        pendingTaskRef.current = taskKey
        setView('touring')
        navigate(targetRoute)
      } else {
        void launchDriver(taskKey)
      }
    },
    [launchDriver, navigate, location.pathname]
  )

  useEffect(() => {
    startTaskRef.current = startTask
  }, [startTask])

  const startTour = useCallback(() => {
    try {
      localStorage.setItem('demo_tour_welcome_dismissed', '1')
    } catch {}
    setView('touring')
    track(events.clickStartDemoTour)
    const completed = new Set(getCompletedTasks())
    const firstIncomplete = ['filter_important', 'detect_alerts', 'bulk_approve'].find(
      (k) => !completed.has(k)
    )
    if (firstIncomplete) {
      setTimeout(() => startTask(firstIncomplete), 300)
    }
  }, [startTask])

  const dismissWelcome = useCallback(() => {
    try {
      localStorage.setItem('demo_tour_welcome_dismissed', '1')
    } catch {}
    setView('touring')
    track(events.clickDismissDemoTourModal)
  }, [])

  const stopTask = useCallback(() => {
    destroyDriver()
    setActiveTaskKey(null)
    setCurrentStep(0)
    setTotalSteps(0)
    track(events.stopDemoTour, { task_key: activeTaskKey })
  }, [destroyDriver, activeTaskKey])

  const restartTour = useCallback(() => {
    resetState()
    try {
      localStorage.removeItem('factorial:timeTrackingViewType')
      localStorage.removeItem('datacollection-time-tracking-collection/v1')
    } catch {}
    setCompletedTasks([])
    setActiveTaskKey(null)
    destroyDriver()
    startTask('filter_important')
  }, [destroyDriver, startTask])

  const closeBookingModal = useCallback(() => {
    setView('touring')
    const completed = new Set(getCompletedTasks())
    const nextTask = ['filter_important', 'detect_alerts', 'bulk_approve'].find(
      (k) => !completed.has(k)
    )
    if (nextTask) {
      setTimeout(() => startTask(nextTask), 300)
    }
  }, [startTask])

  useEffect(() => {
    return () => {
      destroyDriver()
    }
  }, [destroyDriver])

  const bookMeeting = useCallback(() => {
    setView((v) => (v === 'booking' ? 'touring' : v))
    if (calendarUrl) {
      setMeetingModalOpen(true)
    } else {
      window.open(FALLBACK_MEETING_URL, '_blank', 'noopener,noreferrer')
    }
    track(events.clickBookMeetingDemoTour)
  }, [calendarUrl])

  const closeMeetingModal = useCallback(() => {
    setMeetingModalOpen(false)
  }, [])

  return {
    view,
    activeTaskKey,
    completedTasks,
    currentStep,
    showConfetti,
    calendarUrl,
    meetingModalOpen,
    startTour,
    dismissWelcome,
    startTask,
    stopTask,
    restartTour,
    showBookingModal,
    closeBookingModal,
    bookMeeting,
    closeMeetingModal,
  }
}
